import React from "react";
import { Highlight } from "./Highlight";
import { parseContent, ParsedElement } from "../utils/MessageParser";
import { SelectedToken } from "../store/SelectedTokensStore";

interface FormattedMessageProps {
  content: string;
  selectedTokens?: Map<string, SelectedToken>;
}

export const FormattedMessage: React.FC<FormattedMessageProps> = ({
  content,
  selectedTokens = new Map(),
}) => {
  // Parse **bold** text and convert to Highlight components
  const parseBoldText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/);

    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        const boldText = part.replace(/\*\*/g, "");
        return <Highlight key={index}>{boldText}</Highlight>;
      }
      return part;
    });
  };

  // Parse content for tokens and other styled elements
  const parsedContent = parseContent(content, selectedTokens);

  // Render styled elements
  const renderElement = (element: ParsedElement, index: number) => {
    switch (element.type) {
      case "selectedToken":
        if (element.tokenData) {
          return (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-[#E4E4E7] text-black border border-blue-300/30"
            >
              {element.tokenData.icon && (
                <img
                  src={element.tokenData.icon}
                  alt={element.tokenData.symbol}
                  className="w-3 h-3 rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              <span>{element.tokenData.symbol}</span>
              <span className="text-black text-[10px] uppercase">
                {element.tokenData.mode}
              </span>
            </span>
          );
        }
        return <span key={index}>{element.content}</span>;
      default:
        return <span key={index}>{parseBoldText(element.content)}</span>;
    }
  };

  // Convert line breaks to <br> for display
  const renderWithLineBreaks = (elements: ParsedElement[]) => {
    const result: React.ReactNode[] = [];

    elements.forEach((element, elementIndex) => {
      if (element.type === "text") {
        const lines = element.content.split("\n");
        lines.forEach((line, lineIndex) => {
          if (lineIndex > 0) {
            result.push(<br key={`br-${elementIndex}-${lineIndex}`} />);
          }
          if (line) {
            result.push(
              <span key={`text-${elementIndex}-${lineIndex}`}>
                {parseBoldText(line)}
              </span>
            );
          }
        });
      } else {
        result.push(renderElement(element, elementIndex));
      }
    });

    return result;
  };

  return <>{renderWithLineBreaks(parsedContent)}</>;
};
